import React, {
  useCallback,
  createContext,
  DependencyList,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type Listener = (message: MessageEvent) => void;

export enum State {
  CONNECTING = 0,
  OPEN = 1,
  CLOSING = 2,
  CLOSED = 3,
}

type WebsocketContext = {
  state: State;
  addListener(listener: Listener): void;
  removeListener(listener: Listener): void;
  send(message: object): void;
};

const WebsocketContext = createContext<WebsocketContext | null>(null);

const RECONNECT_BASE_DELAY_MS = 3 * 1000;
const RECONNECT_MAX_DELAY_MS = 15 * 60 * 1000;

export const WebsocketProvider: FC<PropsWithChildren<{ url: string }>> = ({
  url,
  children,
}) => {
  const websocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const listenersRef = useRef(new Set<Listener>());
  const shouldReconnectRef = useRef(true);
  const intentionallyClosingSocketsRef = useRef(new WeakSet<WebSocket>());
  const [state, setState] = useState<State>(State.CLOSED);

  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current !== null) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  const addListener = useCallback((listener: Listener) => {
    listenersRef.current.add(listener);
  }, []);

  const removeListener = useCallback((listener: Listener) => {
    listenersRef.current.delete(listener);
  }, []);

  const send = useCallback((message: object) => {
    const websocket = websocketRef.current;

    if (!websocket || websocket.readyState !== State.OPEN) {
      throw new Error('WebSocket connection is not open');
    }

    websocket.send(JSON.stringify(message));
  }, []);

  const connect = useCallback(() => {
    clearReconnectTimeout();

    if (!shouldReconnectRef.current) {
      return () => {};
    }

    const websocket = new WebSocket(url);
    websocketRef.current = websocket;
    setState(State.CONNECTING);

    const handleOpen = () => {
      if (websocketRef.current !== websocket) {
        return;
      }

      reconnectAttemptsRef.current = 0;
      setState(State.OPEN);
      console.debug('WebSocket connection opened', { url: websocket.url });
    };

    const handleMessage = (event: MessageEvent) => {
      for (const listener of listenersRef.current) {
        listener(event);
      }
    };

    const handleError = (event: Event) => {
      if (
        websocketRef.current !== websocket ||
        intentionallyClosingSocketsRef.current.has(websocket)
      ) {
        return;
      }

      console.warn('WebSocket connection error', event);
    };

    const handleClose = (event: CloseEvent) => {
      const isCurrentSocket = websocketRef.current === websocket;
      const isIntentionalClose =
        intentionallyClosingSocketsRef.current.has(websocket);

      intentionallyClosingSocketsRef.current.delete(websocket);

      if (!isCurrentSocket && !isIntentionalClose) {
        return;
      }

      if (isCurrentSocket) {
        websocketRef.current = null;
        setState(State.CLOSED);
      }

      if (isIntentionalClose) {
        return;
      }

      console.debug('WebSocket connection closed', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        url: websocket.url,
      });

      if (!shouldReconnectRef.current) {
        return;
      }

      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(
        RECONNECT_BASE_DELAY_MS * 2 ** attempt,
        RECONNECT_MAX_DELAY_MS,
      );

      reconnectAttemptsRef.current += 1;
      reconnectTimeoutRef.current = window.setTimeout(() => {
        reconnectTimeoutRef.current = null;
        const cleanup = connect();

        if (!shouldReconnectRef.current) {
          cleanup();
        }
      }, delay);
    };

    websocket.addEventListener('open', handleOpen);
    websocket.addEventListener('message', handleMessage);
    websocket.addEventListener('error', handleError);
    websocket.addEventListener('close', handleClose);

    return () => {
      intentionallyClosingSocketsRef.current.add(websocket);

      if (websocketRef.current === websocket) {
        websocketRef.current = null;
      }

      websocket.removeEventListener('open', handleOpen);
      websocket.removeEventListener('message', handleMessage);
      websocket.removeEventListener('error', handleError);
      websocket.removeEventListener('close', handleClose);

      if (
        websocket.readyState === State.CONNECTING ||
        websocket.readyState === State.OPEN
      ) {
        websocket.close();
      }
    };
  }, [clearReconnectTimeout, url]);

  const context = useMemo<WebsocketContext>(
    () => ({
      state,
      addListener,
      removeListener,
      send,
    }),
    [addListener, removeListener, send, state],
  );

  useEffect(() => {
    shouldReconnectRef.current = true;
    let connectionCleanup = () => {};
    const connectTimeout = window.setTimeout(() => {
      if (!shouldReconnectRef.current) {
        return;
      }

      connectionCleanup = connect();
    }, 0);

    return () => {
      shouldReconnectRef.current = false;
      reconnectAttemptsRef.current = 0;
      window.clearTimeout(connectTimeout);
      clearReconnectTimeout();
      connectionCleanup();
    };
  }, [clearReconnectTimeout, connect]);

  return (
    <WebsocketContext.Provider value={context}>
      {children}
    </WebsocketContext.Provider>
  );
};

export type UseWebsocket = {
  state: State;
  send(message: object): void;
};

export function useWebsocket(
  listener: Listener,
  deps: DependencyList,
): UseWebsocket {
  const context = useContext(WebsocketContext);

  if (!context) {
    throw new Error(
      'useWebsocket hook must be used within a WebsocketsProvider',
    );
  }

  useEffect(() => {
    context.addListener(listener);

    return () => {
      context.removeListener(listener);
    };
  }, [context, listener, ...deps]);

  return useMemo(
    () => ({
      state: context.state,
      send: context.send,
    }),
    [context.state, context.send],
  );
}
